import { Button } from '@app/components/various/Button';
import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, Form, FormFieldset, FormGrid, InputField, TextareaField } from '@app/components/various/Form';
import { Modal, StickyModalFooter } from '@app/components/various/Modal';
import { useCountries } from '@app/services/hooks/account/useCountries';
import { useCreateAccountRequest } from '@app/services/hooks/lookbook/useCreateAccountRequest';
import { useCreateLookbookWishlist } from '@app/services/hooks/lookbook/useCreateLookbookWishlist';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import { getPhoneSchema, getStateSchema } from '@app/utils/schemas';
import { Lookbook } from '@typings/@centra';
import { isEmpty } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

interface Props {
  onSuccess: (value: Lookbook.CreateAccountRequestResponse) => void;
  isOpen: boolean;
  onClose: VoidFunction;
  wishlist?: string[];
  lookbookId: string;
}

export interface RequestAccessFormValue {
  additionalInfo: Nullable<string>;
  address: Nullable<string>;
  address2: Nullable<string>;
  city: Nullable<string>;
  companyName: string;
  country: string;
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: Nullable<string>;
  state: Nullable<string>;
  vatNumber: Nullable<string>;
  zipCode: Nullable<string>;
}

export const RequestAccessModal = ({ isOpen, onClose, onSuccess, wishlist = [], lookbookId }: Props) => {
  const { t } = useTranslation(['accounts', 'validation', 'common']);
  const { data: countries } = useCountries();
  const validationSchema = yup.object({
    additionalInfo: yup.string().nullable(),
    address: yup.string().nullable(),
    address2: yup.string().nullable(),
    city: yup.string().nullable(),
    companyName: yup.string().required(t('validation:company_name_hint')),
    country: yup.string().required(t('validation:country_hint')),
    email: yup.string().email(t('validation:invalid_email_hint')).required(t('validation:email_address_hint')),
    firstName: yup.string().required(t('validation:first_name_hint')),
    lastName: yup.string().required(t('validation:last_name_hint')),
    mobileNumber: getPhoneSchema(false),
    state: isDefined(countries) ? getStateSchema(countries.countries) : yup.string().nullable(),
    vatNumber: yup.string().nullable(),
    zipCode: yup.string().nullable(),
  });

  const defaultValues: RequestAccessFormValue = {
    additionalInfo: null,
    address: null,
    address2: null,
    city: null,
    companyName: '',
    country: '',
    email: '',
    firstName: '',
    lastName: '',
    mobileNumber: '',
    state: null,
    vatNumber: null,
    zipCode: null,
  };

  const formMethods = useValidatedForm<RequestAccessFormValue>({ defaultValues, validationSchema });
  const { register, setValue, watch, reset } = formMethods;
  const { isPending: isWishlistPending, mutate: createWishlist } = useCreateLookbookWishlist();
  const { isPending: isRequestAccountPending, mutate: createAccountRequest } = useCreateAccountRequest();
  const selectedCountry = watch('country');
  const isPending = isWishlistPending || isRequestAccountPending;
  const handleCountryChange = () => {
    setValue('state', null);
  };

  const countryOptions = useMemo(() => {
    if (!isDefined(countries)) {
      return [];
    }

    return Object.values(countries.countries).map(({ country, name }) => ({ country, name }));
  }, [countries]);

  const stateOptions = useMemo(() => {
    if (!isDefined(selectedCountry)) {
      return [];
    }

    return Object.values(countries?.countries[selectedCountry]?.states ?? {});
  }, [countries, selectedCountry]);

  const handleSubmit = (value: RequestAccessFormValue) => {
    if (!isEmpty(wishlist)) {
      createWishlist(
        {
          data: {
            products: [...wishlist],
          },
          lookbookId,
        },
        {
          onSuccess: wishlistData => {
            createAccountRequest(
              {
                data: {
                  address: value.address,
                  address2: value.address2,
                  city: value.city,
                  company: value.companyName,
                  country: value.country,
                  email: value.email,
                  firstName: value.firstName,
                  lastName: value.lastName,
                  message: value.additionalInfo,
                  mobileNumber: value.mobileNumber,
                  state: value.state,
                  taxId: value.vatNumber,
                  wishlist: wishlistData.wishlist,
                  zipcode: value.zipCode,
                },
                lookbookId,
              },
              {
                onSuccess: data => {
                  onSuccess(data);
                  reset(defaultValues);
                },
              },
            );
          },
        },
      );

      return;
    }

    createAccountRequest(
      {
        data: {
          address: value.address,
          address2: value.address2,
          city: value.city,
          company: value.companyName,
          country: value.country,
          email: value.email,
          firstName: value.firstName,
          lastName: value.lastName,
          message: value.additionalInfo,
          mobileNumber: value.mobileNumber,
          state: value.state,
          taxId: value.vatNumber,
          zipcode: value.zipCode,
        },
        lookbookId,
      },
      {
        onSuccess: data => {
          onSuccess(data);
          reset(defaultValues);
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      size="large"
      onClose={onClose}
      shouldCloseOnEsc={!isPending}
      shouldCloseOnOverlayClick={!isPending}
      title={t('accounts:request_access.title')}
      description={t('accounts:request_access.subtitle')}
      descriptionDataTestId="requestAccessModal"
      modalClosedCallback={() => reset(defaultValues)}
    >
      <Form formMethods={formMethods} onSubmit={handleSubmit}>
        <FormFieldset>
          <FormGrid cols={2}>
            <InputField label={t('common:company_name')} isRequired {...register('companyName')} />
            <InputField label={t('common:email')} isRequired {...register('email')} />
            <InputField label={t('common:first_name')} isRequired {...register('firstName')} />
            <InputField label={t('common:last_name')} isRequired {...register('lastName')} />
            <AutocompleteField name="country" label={t('common:country')} isRequired onChange={handleCountryChange}>
              {countryOptions.map(({ country, name }) => (
                <Option key={country} value={country}>
                  {name}
                </Option>
              ))}
            </AutocompleteField>
            <InputField label={t('common:mobile_number')} isRequired={false} {...register('mobileNumber')} />
          </FormGrid>
        </FormFieldset>
        <FormFieldset className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-wider">{t('common:address')}</span>
          <FormGrid cols={2}>
            {!isEmpty(stateOptions) && (
              <AutocompleteField name="state" label={t('common:state')} isRequired>
                {stateOptions.map(({ state, name }) => (
                  <Option key={state} value={state}>
                    {name}
                  </Option>
                ))}
              </AutocompleteField>
            )}
            <InputField label={t('common:address')} isRequired={false} {...register('address')} />
            <InputField label={t('common:address_continued')} isRequired={false} {...register('address2')} />
            <InputField label={t('common:city')} isRequired={false} {...register('city')} />
            <InputField label={t('common:zip_code')} isRequired={false} {...register('zipCode')} />
            <InputField label={t('common:vat_number')} isRequired={false} {...register('vatNumber')} />
          </FormGrid>
        </FormFieldset>
        <FormFieldset className="flex flex-col gap-2 pb-10">
          <span className="text-xs uppercase tracking-wider">{t('common:message')}</span>
          <TextareaField label={t('common:additional_info')} isRequired={false} {...register('additionalInfo')} />
        </FormFieldset>
        <StickyModalFooter>
          <div className="flex justify-end gap-2">
            <Button color="secondary" variant="ghost" disabled={isPending} onClick={onClose}>
              {t('common:cancel')}
            </Button>
            <Button type="submit" isLoading={isPending}>
              {t('accounts:request_account')}
            </Button>
          </div>
        </StickyModalFooter>
      </Form>
    </Modal>
  );
};
